// Step 2 of MakeABookingDialog. For recurring adhoc or slot bookings, surfaces a list of every
// upcoming occurrence (up to 52 weeks from today) with availability status. The "Book" CTA is
// gated on every occurrence being clear — per spec, a recurring booking cannot proceed if any
// occurrence clashes with another booking's core or buffer (R3/R5/R6/R7).
//
// Replaces the previous DraggableClearway grid that visualised every week as a calendar
// column. Members and admins gave consistent feedback that the calendar visualisation was
// awkward to scan; the list pattern matches RecurrenceExceptionsManager and the rest of the
// recurrence UI.
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import OccurrenceCountPicker from "../components/OccurrenceCountPicker";
import { defaultButtonClasses, secondaryButtonClasses } from "@/constants/ui";
import { LocationService } from "@/services/LocationService";
import { RoomService } from "@/services/RoomService";
import { BookingOccurrenceList } from "@/components/tpr/BookingOccurrenceList/BookingOccurrenceList";
import { useFeatureFlagsQuery } from "@/hooks/useFeatureFlagsQuery";
import { useMakeABookingDialog } from "../useMakeABookingDialog";
import { BookingService } from "@/services/BookingService";
import { buildFlexBookingParams } from "../flexBooking";
import { buildSlotSummary } from "@/utils/slotSummary";
import { earliestSlotPullForwardDate, slotPullForwardFirstWeekOfMonthOnly } from "@/utils/slotPullForward";
import type { Location } from "@/types/Location";
import type { Room } from "@/types/Room";

export const RepeatSetupScreen = () => {
  const {
    formData,
    updateFormData,
    isSubmitting,
    error,
    submitBooking,
    goBack,
    onClose,
    currentUser,
    isElevatedUser,
  } = useMakeABookingDialog();

  // R24a "Start Slot Here" is gated by the "Early Slot Starts" flag (now default ON).
  const { data: featureFlags } = useFeatureFlagsQuery();
  const earlySlotStartsEnabled = featureFlags?.earlySlotStartsEnabled ?? false;
  // R24a role-aware notice: occurrences before this date are too soon to pull forward to (PM 7 days,
  // admin tomorrow). The server re-validates regardless.
  const earliestSlotStartDate = earlySlotStartsEnabled ? earliestSlotPullForwardDate(currentUser) : undefined;
  // R24a — practice managers may only pull a slot start to the first week of a month; admins are
  // unrestricted. Hides the "Start Slot Here" button on weeks 2–4 of the gap.
  const firstWeekOfMonthOnly = earlySlotStartsEnabled ? slotPullForwardFirstWeekOfMonthOnly(currentUser) : undefined;

  const [location, setLocation] = useState<Location | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [allAvailable, setAllAvailable] = useState(false);
  // Task 6 — surfaced from BookingOccurrenceList's elevated-aware totals (Task 5): true when at
  // least one active occurrence was only let through for THIS elevated user because its clashes
  // were buffer-only. Purely cosmetic (relabels the CTA); the actual gate stays `allAvailable`.
  const [bufferOverrideNeeded, setBufferOverrideNeeded] = useState(false);

  // Slot = 52 weekly occurrences (one year). Adhoc-recurring = 12 occurrences (~3 months).
  // Used as the PREVIEW length for an ongoing series, and as the default when the operator switches
  // to a fixed count.
  const defaultPreviewCount = formData.bookingType === "slot" ? 52 : 12;

  // The chosen series length. `undefined` = ongoing (no stored count), which is how the rest of the
  // system represents an indefinite recurrence. Previously there was no control at all: creation
  // always produced an ongoing series while previewing a hardcoded 12, so the preview and the
  // created booking disagreed and the operator could not choose either. Slots are intrinsically
  // indefinite (R13), so the picker is offered for adhoc recurrences only.
  const chosenCount = formData.recurrencePattern?.occurrencesToCheck;
  const occurrenceCount = chosenCount ?? defaultPreviewCount;
  const showCountPicker = formData.bookingType !== "slot";

  const setOccurrenceCount = (next: number | undefined) =>
    updateFormData({
      recurrencePattern: {
        ...(formData.recurrencePattern ?? { label: "Weekly", frequency: "weekly", interval: 1 }),
        occurrencesToCheck: next,
      },
    });

  // Fetch location + room metadata for the header. Availability checking is handled inside
  // BookingOccurrenceList; we just need the display data here.
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (formData.locationId) {
          const fetchedLocation = await new LocationService().getLocationById(formData.locationId);
          if (fetchedLocation) setLocation(fetchedLocation);
        }
        if (formData.roomId) {
          const fetchedRoom = await new RoomService().getRoomById(formData.roomId);
          if (fetchedRoom) setRoom(fetchedRoom);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [formData.locationId, formData.roomId]);

  const handleBookFlex = useCallback(
    async (occurrenceDate: Date): Promise<string> => {
      const bookingService = new BookingService();
      const booking = await bookingService.createNewBooking(
        buildFlexBookingParams({
          locationId: formData.locationId,
          roomId: formData.roomId,
          memberId: formData.memberId,
          time: formData.time,
          durationMinutes: formData.durationMinutes,
          date: occurrenceDate,
        }),
      );
      bookingService.api.clearCache();
      if (!booking.shortCode) throw new Error("Booking created without a reference");
      return booking.shortCode;
    },
    [formData.locationId, formData.roomId, formData.memberId, formData.time, formData.durationMinutes],
  );

  const handleBook = async () => {
    if (!allAvailable) return;
    await submitBooking();
  };

  if (!formData.date || !formData.time || !formData.locationId || !formData.roomId) {
    return (
      <div className="w-full flex flex-col bg-white rounded-xl my-4">
        <div className="flex items-center justify-center flex-1 p-6">
          <span className="text-gray-500">Missing required booking information</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full flex flex-col bg-white rounded-xl my-4">
        <div className="flex items-center justify-center flex-1 p-6">
          <span className="text-gray-500">Loading availability...</span>
        </div>
      </div>
    );
  }

  const renderCTAs = () => (
    <div className="flex flex-col gap-2 mt-6">
      <Button
        type="button"
        className={`w-full ${defaultButtonClasses}`}
        onClick={handleBook}
        disabled={!allAvailable || isSubmitting}
        data-testid="repeat-setup-book-button"
      >
        {isSubmitting ? "Creating booking..." : bufferOverrideNeeded ? "Book (override required)" : "Book"}
      </Button>
      <Button
        type="button"
        variant="outline"
        className={`w-full ${secondaryButtonClasses}`}
        onClick={() => goBack()}
        disabled={isSubmitting}
      >
        Back
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="w-full text-gray-600"
        onClick={() => onClose?.()}
        disabled={isSubmitting}
      >
        Close
      </Button>
    </div>
  );

  const slot =
    formData.bookingType === "slot"
      ? buildSlotSummary(formData.date, formData.time, formData.durationMinutes, formData.slotStartOverride)
      : null;

  const summary = (
    <div className="p-4 bg-white/80 rounded-lg border border-gray-200">
      <div className="text-sm font-medium mb-2">Booking summary</div>
      <ul className="text-sm text-gray-700 space-y-1">
        <li>{location?.name ?? formData.locationName}</li>
        <li>{room?.name ?? formData.roomName}</li>
        {slot ? (
          <>
            <li>Weekly slot — recurs every week</li>
            <li>{slot.scheduleLine}</li>
            <li>{slot.startLine}</li>
            {slot.noticeLine && (
              <li className="text-xs text-gray-500 mt-1" data-testid="slot-notice-line">
                {slot.noticeLine}
              </li>
            )}
          </>
        ) : (
          <>
            <li>
              {formData.time.slice(0, 2)}:{formData.time.slice(2)} for {formData.durationMinutes} minutes
            </li>
            <li>{`Recurring: ${formData.recurrencePattern?.label ?? "weekly"}`}</li>
            <li>{chosenCount ? `${chosenCount} occurrences` : "No end date (ongoing)"}</li>
          </>
        )}
      </ul>
    </div>
  );

  return (
    <div className="w-full flex flex-col bg-white rounded-xl my-4">
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50 rounded-t-2xl">
        <h2 className="text-2xl font-semibold mb-2">
          {formData.bookingType === "slot" ? "Confirm your slot" : "Confirm recurring booking"}
        </h2>
      </div>

      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">
            {formData.bookingType === "slot" ? "Confirm your slot" : "Confirm recurring booking"}
          </h2>
          {summary}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-medium text-red-800">Booking could not be created</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          )}
          <div className="mt-auto pt-6">{renderCTAs()}</div>
        </div>

        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          {showCountPicker && (
            <div className="mb-4">
              <OccurrenceCountPicker
                value={chosenCount}
                onChange={setOccurrenceCount}
                previewWhenOngoing={defaultPreviewCount}
              />
            </div>
          )}
          <BookingOccurrenceList
            roomId={formData.roomId}
            memberId={formData.memberId}
            startDate={formData.date}
            startTime={formData.time}
            durationMinutes={formData.durationMinutes}
            frequency={formData.recurrencePattern?.frequency === "monthly" ? "monthly" : "weekly"}
            interval={formData.recurrencePattern?.interval ?? 1}
            occurrenceCount={occurrenceCount}
            slotStartDate={formData.slotStartOverride ?? slot?.startDate}
            onBookFlex={slot ? handleBookFlex : undefined}
            onStartSlotHere={
              slot && earlySlotStartsEnabled
                ? (occurrenceDate) => updateFormData({ slotStartOverride: occurrenceDate })
                : undefined
            }
            earliestSlotStartDate={earliestSlotStartDate}
            firstWeekOfMonthOnly={firstWeekOfMonthOnly}
            isElevated={isElevatedUser}
            onAvailabilityChange={(available, totals) => {
              setAllAvailable(available);
              setBufferOverrideNeeded(totals.bufferOverrideNeeded);
            }}
          />
        </div>
      </div>

      <div className="lg:hidden flex-1 bg-gray-50 p-4 flex flex-col">
        {summary}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="font-medium text-red-800">Booking could not be created</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        )}
        <div className="mt-4">
          <BookingOccurrenceList
            roomId={formData.roomId}
            memberId={formData.memberId}
            startDate={formData.date}
            startTime={formData.time}
            durationMinutes={formData.durationMinutes}
            frequency={formData.recurrencePattern?.frequency === "monthly" ? "monthly" : "weekly"}
            interval={formData.recurrencePattern?.interval ?? 1}
            occurrenceCount={occurrenceCount}
            slotStartDate={formData.slotStartOverride ?? slot?.startDate}
            onBookFlex={slot ? handleBookFlex : undefined}
            onStartSlotHere={
              slot && earlySlotStartsEnabled
                ? (occurrenceDate) => updateFormData({ slotStartOverride: occurrenceDate })
                : undefined
            }
            earliestSlotStartDate={earliestSlotStartDate}
            firstWeekOfMonthOnly={firstWeekOfMonthOnly}
            isElevated={isElevatedUser}
            onAvailabilityChange={(available, totals) => {
              setAllAvailable(available);
              setBufferOverrideNeeded(totals.bufferOverrideNeeded);
            }}
          />
        </div>
        {renderCTAs()}
      </div>
    </div>
  );
};

export default RepeatSetupScreen;
